import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PentairTextField } from '../../../theme/components/pentair-text-field';
import { FormOptions, useFormData } from '../../form/use-formdata';
import { requiredEmail, requiredPhoneNumber, requiredString } from '../../form/validators';

interface UserAttributes {
  given_name?: string;
  middle_name?: string;
  family_name?: string;
  email?: string;
  phone_number?: string;
}

const defaultRecord: UserAttributes = {};
const formOptions: FormOptions<UserAttributes> = {
  validations: {
    given_name: requiredString,
    family_name: requiredString,
    email: requiredEmail,
    phone_number: requiredPhoneNumber,
  },
};

export const AccountForm: React.FC<{
  initialRecord?: UserAttributes | null;
  disabled?: boolean;
  onSubmit?: (record: UserAttributes) => Promise<any>;
}> = ({ disabled, onSubmit, initialRecord = { ...defaultRecord } }) => {

  const { t } = useTranslation();

  formOptions.onSubmit = onSubmit;
  formOptions.disabled = disabled;
  const { change, record, validationErrors, submitForm } = useFormData<UserAttributes>(initialRecord, formOptions);

  return (
    <Box component="form" onSubmit={submitForm} sx={{ marginBottom: [3, 3, 0] }}>
      <PentairTextField
        label={t('iam:givenName.label', { defaultValue: 'First name' })}
        description={t('iam:givenName.description')}
        placeholder={t('iam:givenName.placeholder', { defaultValue: 'First name' })}
        value={record?.given_name || ''}
        onChange={(e) => change('given_name', e.target.value)}
        variant="standard"
        margin="dense"
        error={!!validationErrors['given_name']}
        helperText={validationErrors['given_name']}
        disabled={disabled}
      />

      <PentairTextField
        label={t('iam:middleName.label', { defaultValue: 'Middle name' })}
        description={t('iam:middleName.description')}
        placeholder={t('iam:middleName.placeholder', { defaultValue: 'Middle name' })}
        value={record?.middle_name || ''}
        onChange={(e) => change('middle_name', e.target.value)}
        variant="standard"
        margin="dense"
        error={!!validationErrors['middle_name']}
        helperText={validationErrors['middle_name']}
        disabled={disabled}
      />

      <PentairTextField
        label={t('iam:familyName.label', { defaultValue: 'Last name' })}
        description={t('iam:familyName.description')}
        placeholder={t('iam:familyName.placeholder', { defaultValue: 'Last name' })}
        value={record?.family_name || ''}
        onChange={(e) => change('family_name', e.target.value)}
        variant="standard"
        margin="dense"
        error={!!validationErrors['family_name']}
        helperText={validationErrors['family_name']}
        disabled={disabled}
      />

      <PentairTextField
        label={t('iam:email.label', { defaultValue: 'Email' })}
        description={t('iam:email.description')}
        placeholder={t('iam:email.placeholder', { defaultValue: 'Email' })}
        value={record?.email || ''}
        onChange={(e) => change('email', e.target.value)}
        variant="standard"
        margin="dense"
        error={!!validationErrors['email']}
        helperText={validationErrors['email']}
        disabled={disabled}
      />

      <PentairTextField
        label={t('iam:phoneNumber.label', { defaultValue: 'Phone number' })}
        description={t('iam:phoneNumber.description')}
        placeholder={t('iam:phone_number.placeholder', { defaultValue: 'Phone number' })}
        value={record?.phone_number || ''}
        onChange={(e) => change('phone_number', e.target.value)}
        variant="standard"
        margin="dense"
        error={!!validationErrors['phone_number']}
        helperText={validationErrors['phone_number']}
        disabled={disabled}
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={disabled}
        size="small"
        sx={{ marginTop: 1, marginLeft: 'auto', marginBottom: [3, 3, 0], float: ['right', 'right', 'none'] }}
      >
        {t('iam:buttonLabel.updateAccount', { defaultValue: 'Save changes' })}
      </Button>
    </Box>
  );
};
